import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import moment from 'moment';
import { PIPELINE } from './VenuePipelineStrip';

const fmt = (d) => (d ? moment(d).format('MMM D, YYYY') : '—');
const money = (n) => (n === null || n === undefined || n === '' || n === 0) ? '—' : `$${Number(n).toLocaleString()}`;

const statusLabel = (status) => {
	const def = PIPELINE.find((p) => p.key === status);
	return def ? def.label : (status || 'Draft');
};

const styles = StyleSheet.create({
	page: {
		padding: '32 40',
		fontSize: 9,
		fontFamily: 'Helvetica',
		color: '#1a1a1a'
	},
	/* ── header ── */
	header: {
		marginBottom: 16,
		borderBottom: '1.5 solid #1a1a1a',
		paddingBottom: 10,
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'flex-end'
	},
	title: {
		fontSize: 16,
		fontFamily: 'Helvetica-Bold',
		marginBottom: 2
	},
	subtitle: {
		fontSize: 9,
		color: '#6b7280'
	},
	generated: {
		fontSize: 8,
		color: '#9ca3af',
		textAlign: 'right'
	},
	/* ── filter summary ── */
	filterSummary: {
		fontSize: 8,
		color: '#6b7280',
		marginBottom: 10,
		fontStyle: 'italic'
	},
	/* ── table ── */
	table: {
		width: '100%'
	},
	thead: {
		flexDirection: 'row',
		backgroundColor: '#f9fafb',
		borderBottom: '1 solid #e5e7eb',
		borderTop: '1 solid #e5e7eb'
	},
	th: {
		fontSize: 7,
		fontFamily: 'Helvetica-Bold',
		color: '#6b7280',
		textTransform: 'uppercase',
		letterSpacing: 0.4,
		padding: '6 8'
	},
	tr: {
		flexDirection: 'row',
		borderBottom: '1 solid #f3f4f6'
	},
	trAlt: {
		backgroundColor: '#fafafa'
	},
	td: {
		fontSize: 8,
		padding: '7 8',
		color: '#111827'
	},
	tdMuted: {
		color: '#6b7280'
	},
	statusPill: {
		fontSize: 7,
		fontFamily: 'Helvetica-Bold',
		padding: '2 5',
		borderRadius: 4
	},
	/* ── footer ── */
	footer: {
		position: 'absolute',
		bottom: 20,
		left: 40,
		right: 40,
		flexDirection: 'row',
		justifyContent: 'space-between',
		borderTop: '1 solid #e5e7eb',
		paddingTop: 5
	},
	footerText: {
		fontSize: 7,
		color: '#9ca3af'
	}
});

/* Column definitions — width must total 100% */
const COLS = [
	{ label: 'Venue',       key: 'name',        w: '20%', bold: true },
	{ label: 'Brand',       key: 'brand',       w: '11%' },
	{ label: 'Location',    key: 'location',    w: '14%' },
	{ label: 'Status',      key: 'status',      w: '12%' },
	{ label: 'Sent',        key: 'sent',        w: '9%'  },
	{ label: 'Responded',   key: 'responded',   w: '9%'  },
	{ label: 'Room Rate',   key: 'rate',        w: '10%' },
	{ label: 'Expiration',  key: 'expiration',  w: '10%' },
	{ label: 'Rooms Hold',  key: 'hold',        w: '5%'  },
];

const getCell = (pv, key) => {
	const venue = pv.venue || {};
	switch (key) {
		case 'name':       return venue.name || 'Unnamed Venue';
		case 'brand':      return pv.brandName || '—';
		case 'location':   return venue.location || '—';
		case 'status':     return statusLabel(pv.status || 'Draft');
		case 'sent':       return fmt(pv.sentDate);
		case 'responded':  return fmt(pv.responseDate);
		case 'rate':       return money(pv.quotedRoomRate);
		case 'expiration': return fmt(pv.proposalExpirationDate);
		case 'hold':       return pv.roomsOnHold || '—';
		default:           return '—';
	}
};

/**
 * VenueListPDF — @react-pdf/renderer document for the full venues list.
 * Props:
 *   venues      — filtered array of proposalVenue objects
 *   requestName — optional string for the title
 *   filterSummary — optional string describing active filters
 */
const VenueListPDF = ({ venues = [], requestName = 'Proposal Request', filterSummary = '' }) => {
	const today = moment().format('MMMM D, YYYY');

	return (
		<Document title={`${requestName} — Venues`}>
			<Page size="LETTER" orientation="landscape" style={styles.page}>

				{/* ── Header ── */}
				<View style={styles.header} fixed>
					<View>
						<Text style={styles.title}>{requestName}</Text>
						<Text style={styles.subtitle}>Venues — {venues.length} {venues.length === 1 ? 'venue' : 'venues'}</Text>
					</View>
					<Text style={styles.generated}>Generated {today}</Text>
				</View>

				{/* ── Active filter summary ── */}
				{filterSummary ? (
					<Text style={styles.filterSummary}>Filters: {filterSummary}</Text>
				) : null}

				{/* ── Table ── */}
				<View style={styles.table}>
					{/* thead */}
					<View style={styles.thead} fixed>
						{COLS.map((col) => (
							<Text key={col.key} style={[styles.th, { width: col.w }]}>
								{col.label}
							</Text>
						))}
					</View>

					{/* rows */}
					{venues.map((pv, idx) => (
						<View key={pv._id || idx} style={[styles.tr, idx % 2 === 1 ? styles.trAlt : null]} wrap={false}>
							{COLS.map((col) => (
								<Text
									key={col.key}
									style={[
										styles.td,
										{ width: col.w },
										col.bold ? { fontFamily: 'Helvetica-Bold' } : styles.tdMuted,
										col.key === 'rate' ? { textAlign: 'right' } : null
									]}
								>
									{getCell(pv, col.key)}
								</Text>
							))}
						</View>
					))}

					{/* empty state */}
					{venues.length === 0 && (
						<View style={{ padding: '20 8' }}>
							<Text style={{ fontSize: 9, color: '#9ca3af', fontStyle: 'italic' }}>No venues match the current filters.</Text>
						</View>
					)}
				</View>

				{/* ── Footer ── */}
				<View style={styles.footer} fixed>
					<Text style={styles.footerText}>{requestName} — Venues</Text>
					<Text
						style={styles.footerText}
						render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
					/>
					<Text style={styles.footerText}>{today}</Text>
				</View>

			</Page>
		</Document>
	);
};

export default VenueListPDF;
